import { useCallback, useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Button, Text } from "@/components/ui";
import { Field, Input } from "@/components/ui/field";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";

type TestView = { at: string; ok: boolean; error: string | null; simulated?: boolean };

export type EmailAccountSettings =
  | { configured: false }
  | {
      configured: true;
      /** MT-1 INFRA-024 S1: absent from a pre-S1 server (treated as unknown, nothing shown). */
      state?: "untested" | "test_failed" | "live" | "failing";
      live?: boolean;
      fromName: string;
      fromEmail: string;
      replyTo: string | null;
      apiKeyHint: string;
      updatedAt: string;
      lastTest: TestView | null;
      pending?: null | {
        fromName: string;
        fromEmail: string;
        replyTo: string | null;
        apiKeyHint: string;
        savedAt: string;
        lastTest: TestView | null;
      };
      failing?: null | { since: string; error: string | null };
      waitingCount?: number;
    };

type TestResult = {
  ok: boolean;
  mocked: boolean;
  error: string | null;
  tested?: "current" | "pending";
  promoted?: boolean;
  view?: EmailAccountSettings;
};

export type EmailAccountFetcher = (path: string, init?: RequestInit) => Promise<Response>;

/** COMM-10: dates follow the app's language, not the browser's. */
export function useFormatDate() {
  const locale = useLocale();
  return useCallback(
    (iso: string) =>
      new Date(iso).toLocaleString(locale === "hi" ? "hi-IN" : "en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    [locale],
  );
}

/** MT-1 INFRA-024 S1: whether the account sends, and what is waiting on it. */
function AccountStatus({ settings }: { settings: EmailAccountSettings }) {
  const t = useTranslations("emailSettings");
  const formatDate = useFormatDate();
  if (!settings.configured || settings.live === undefined) return null;
  const pending = settings.pending ?? null;
  const failing = settings.failing ?? null;
  return (
    <div className="space-y-2" data-testid="email-settings-status">
      {failing ? (
        <Text role="alert" className="text-[var(--danger)]" data-testid="email-settings-failing">
          {t("failing", { date: formatDate(failing.since), error: failing.error ?? "" })}
        </Text>
      ) : settings.live ? (
        <p
          className="inline-block rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800 dark:bg-green-900 dark:text-green-100"
          data-testid="email-settings-live"
        >
          {t("live")}
        </p>
      ) : (
        <Text data-testid="email-settings-not-live">{t("notLive")}</Text>
      )}
      {pending ? (
        <Text data-testid="email-settings-pending">
          {pending.lastTest && !pending.lastTest.ok
            ? t("pendingTestFailed", { error: pending.lastTest.error ?? "" })
            : t("pendingSaved", { hint: pending.apiKeyHint, fromEmail: pending.fromEmail })}
        </Text>
      ) : null}
      {settings.waitingCount ? (
        <Text data-testid="email-settings-waiting">
          {t("waiting", { count: settings.waitingCount })}
        </Text>
      ) : null}
    </div>
  );
}

/**
 * INFRA-006: a school's own Resend account — the form the school admin uses, and (Plan 3 S2) the
 * platform admin on the school page. The key is write-only — the API returns only a hint, and the
 * field is cleared after every save. MT-1 INFRA-024 S1: a save on a live account is pending until
 * its own test passes; the form shows which config is live.
 */
export function EmailAccountForm({
  endpoint,
  fetcher,
  onLoaded,
  schoolName,
}: {
  /** `/v1/tenant/email-settings`, or the platform route for one school (Plan 3 S2). */
  endpoint: string;
  fetcher: EmailAccountFetcher;
  onLoaded?: (settings: EmailAccountSettings) => void;
  /** TEN-007 S11 (FR-24): the sender-name hint's example is the school's own name. */
  schoolName?: string;
}) {
  const t = useTranslations("emailSettings");
  const tRoot = useTranslations();
  const formatDate = useFormatDate();
  const errorText = (err: unknown, fallbackKey: string) =>
    apiErrorText(err, tRoot, fallbackKey, { namespace: "emailSettings.errors" });
  const [settings, setSettings] = useState<EmailAccountSettings | null>(null);
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<"save" | "test" | "remove" | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);

  function apply(next: EmailAccountSettings) {
    setSettings(next);
    onLoaded?.(next);
    setFromName(next.configured ? next.fromName : "");
    setFromEmail(next.configured ? next.fromEmail : "");
    setReplyTo(next.configured ? (next.replyTo ?? "") : "");
    setApiKey("");
  }

  useEffect(() => {
    void (async () => {
      try {
        const res = await fetcher(endpoint);
        if (!res.ok) throw await readApiErrorResponse(res);
        const json = (await res.json()) as { data: EmailAccountSettings };
        apply(json.data);
      } catch (err) {
        setError(
          apiErrorText(err, tRoot, "emailSettings.loadFailed", {
            namespace: "emailSettings.errors",
          }),
        );
      }
    })();
    // The form loads once per endpoint (apply() only sets state).
  }, [tRoot, endpoint, fetcher]);

  async function handleSave(event: React.FormEvent) {
    event.preventDefault();
    setBusy("save");
    setError(null);
    setNotice(null);
    setTestResult(null);
    try {
      const res = await fetcher(endpoint, {
        method: "PUT",
        body: JSON.stringify({
          fromName: fromName.trim(),
          fromEmail: fromEmail.trim(),
          replyTo: replyTo.trim() || null,
          ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        }),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      const json = (await res.json()) as { data: EmailAccountSettings };
      apply(json.data);
      setNotice(json.data.configured && json.data.pending ? t("savedPending") : t("saved"));
    } catch (err) {
      setError(errorText(err, "emailSettings.saveFailed"));
    } finally {
      setBusy(null);
    }
  }

  async function handleTest() {
    setBusy("test");
    setError(null);
    setTestResult(null);
    try {
      const res = await fetcher(`${endpoint}/test`, { method: "POST" });
      if (!res.ok) throw await readApiErrorResponse(res);
      const json = (await res.json()) as { data: TestResult };
      const result = json.data;
      if (result.view) setSettings(result.view);
      setTestResult(
        !result.ok
          ? result.tested === "pending"
            ? t("pendingTestFailed", { error: result.error ?? "" })
            : t("testFailed", { error: result.error ?? "" })
          : result.mocked
            ? t("testMocked")
            : result.promoted
              ? t("testPromoted")
              : t("testOk"),
      );
    } catch (err) {
      setError(errorText(err, "emailSettings.saveFailed"));
    } finally {
      setBusy(null);
    }
  }

  async function handleRemove() {
    if (!window.confirm(t("removeConfirm"))) return;
    setBusy("remove");
    setError(null);
    try {
      const res = await fetcher(endpoint, { method: "DELETE" });
      if (!res.ok) throw await readApiErrorResponse(res);
      apply({ configured: false });
      setNotice(null);
      setTestResult(null);
    } catch (err) {
      setError(errorText(err, "emailSettings.saveFailed"));
    } finally {
      setBusy(null);
    }
  }

  if (!settings && !error) {
    return <Text tone="muted">{t("loading")}</Text>;
  }

  return (
    <div className="space-y-6" data-testid="email-account-form">
      {settings && !settings.configured ? (
        <div
          className="rounded-lg border border-[var(--accent-border)] bg-[var(--accent-soft)] p-4 text-sm"
          data-testid="email-settings-not-configured"
        >
          <p className="font-medium">{t("notConfigured")}</p>
          <p className="mt-3 font-medium">{t("stepsTitle")}</p>
          <ol className="mt-1 list-decimal space-y-1 pl-5">
            <li>{t("step1")}</li>
            <li>{t("step2")}</li>
            <li>{t("step3")}</li>
            <li>{t("step4")}</li>
          </ol>
        </div>
      ) : null}

      {settings ? <AccountStatus settings={settings} /> : null}

      {settings?.configured && settings.lastTest ? (
        <Text tone="muted" data-testid="email-settings-last-test">
          {settings.lastTest.ok
            ? t("lastTestOk", { date: formatDate(settings.lastTest.at) })
            : t("lastTestFailed", {
                date: formatDate(settings.lastTest.at),
                error: settings.lastTest.error ?? "",
              })}
        </Text>
      ) : null}

      <form onSubmit={handleSave} className="space-y-4" noValidate>
        <Field
          label={t("fromName")}
          hint={t("fromNameHint", { school: schoolName ?? "" })}
          required
        >
          <Input value={fromName} onChange={(e) => setFromName(e.target.value)} maxLength={80} />
        </Field>
        <Field label={t("fromEmail")} hint={t("fromEmailHint")} required>
          <Input type="email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} />
        </Field>
        <Field label={t("replyTo")} hint={t("replyToHint")}>
          <Input type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} />
        </Field>
        <Field
          label={t("apiKey")}
          hint={
            settings?.configured
              ? t("apiKeyHintSaved", { hint: settings.apiKeyHint })
              : t("apiKeyHintNew")
          }
          required={!settings?.configured}
        >
          <Input
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        </Field>

        {error ? (
          <Text role="alert" className="text-[var(--danger)]">
            {error}
          </Text>
        ) : null}
        <div aria-live="polite">
          {notice ? <Text className="text-green-700 dark:text-green-400">{notice}</Text> : null}
          {testResult ? <Text data-testid="email-settings-test-result">{testResult}</Text> : null}
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            type="submit"
            variant="accent"
            disabled={busy !== null}
            data-testid="email-settings-save"
          >
            {busy === "save" ? t("saving") : t("save")}
          </Button>
          {settings?.configured ? (
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={busy !== null}
                onClick={() => void handleTest()}
                data-testid="email-settings-test"
              >
                {busy === "test" ? t("testing") : t("test")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={busy !== null}
                onClick={() => void handleRemove()}
                data-testid="email-settings-remove"
              >
                {t("remove")}
              </Button>
            </>
          ) : null}
        </div>
      </form>
    </div>
  );
}
